import React, { useState, useMemo } from 'react';
import PageTemplate from '../components/Common/PageTemplate';
import { ChevronDown, ChevronUp, ExternalLink, FileText, GitBranch, Users, GraduationCap, FlaskRound, Settings, Search, Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BirimFaaliyetleri = () => {
  const { t } = useTranslation();
  const processData = t('unit_activities_data', { returnObjects: true });
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // İkon mapping
  const iconMapping = {
    "fa-sitemap": <Settings className="w-6 h-6" />,
    "fa-user-graduate": <GraduationCap className="w-6 h-6" />,
    "fa-flask": <FlaskRound className="w-6 h-6" />,
    "fa-users": <Users className="w-6 h-6" />
  };

  // Renk mapping
  const colorMapping = {
    "fa-sitemap": { color: "from-blue-600 to-indigo-700", bgColor: "bg-blue-50" },
    "fa-user-graduate": { color: "from-green-600 to-emerald-700", bgColor: "bg-green-50" },
    "fa-flask": { color: "from-purple-600 to-violet-700", bgColor: "bg-purple-50" },
    "fa-users": { color: "from-orange-600 to-red-700", bgColor: "bg-orange-50" }
  };

  // Filtrelenmiş veri
  const filteredData = useMemo(() => {
    if (!processData || !Array.isArray(processData)) return [];
    return processData.map(category => ({
      ...category,
      items: category.items.filter(item => {
        const matchesSearch = !searchTerm || 
          item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.code.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesCategory = !selectedCategory || category.category === selectedCategory;
        
        return matchesSearch && matchesCategory;
      })
    })).filter(category => 
      !selectedCategory || category.category === selectedCategory || category.items.length > 0
    );
  }, [searchTerm, selectedCategory, processData]);

  const toggleCategory = (categoryIndex) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryIndex]: !prev[categoryIndex]
    }));
  };

  const toggleItem = (categoryIndex, itemIndex) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const totalProcesses = useMemo(() => {
    if (!processData || !Array.isArray(processData)) return 0;
    return processData.reduce((sum, category) => sum + category.items.length, 0);
  }, [processData]);

  const filteredProcesses = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return 0;
    return filteredData.reduce((sum, category) => sum + category.items.length, 0);
  }, [filteredData]);

  return (
    <PageTemplate
      title={t('unit_activities.title')}
      subtitle={t('unit_activities.subtitle')}
    >
      <div className="space-y-8">
        {/* Başlık ve İstatistikler */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('unit_activities.process_management_title')}</h2>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-6">
            {t('unit_activities.process_management_description')}
          </p>

          {/* İstatistik Kartları */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200">
              <div className="text-2xl font-bold text-blue-600">{totalProcesses}</div>
              <div className="text-sm text-gray-600">{t('unit_activities.total_processes')}</div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200">
              <div className="text-2xl font-bold text-green-600">{processData?.length || 0}</div>
              <div className="text-sm text-gray-600">{t('unit_activities.main_categories')}</div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200">
              <div className="text-2xl font-bold text-purple-600">ISO 9001</div>
              <div className="text-sm text-gray-600">{t('unit_activities.quality_standard')}</div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200">
              <div className="text-2xl font-bold text-orange-600">24/7</div>
              <div className="text-sm text-gray-600">{t('unit_activities.access')}</div>
            </div>
          </div>
        </div>

        {/* Arama ve Filtreleme */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('unit_activities.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="lg:w-80 relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="">{t('unit_activities.all_categories')}</option>
                {processData && processData.map((category, index) => (
                  <option key={index} value={category.category}>
                    {category.category}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Sonuç sayısı */}
          {(searchTerm || selectedCategory) && (
            <div className="mt-4 text-sm text-gray-600">
              {filteredProcesses} {t('unit_activities.processes_found')}
              {searchTerm && ` ${t('unit_activities.for_search')} "${searchTerm}"`}
              {selectedCategory && ` ${t('unit_activities.in_category')} "${selectedCategory}"`}
            </div>
          )}
        </div>

        {/* Kategori Kartları */}
        <div className="space-y-6">
          {filteredData && filteredData.map((category, categoryIndex) => {
            const colors = colorMapping[category.icon];
            const icon = iconMapping[category.icon];
            
            // Eğer kategori items'ı yoksa gösterme
            if (category.items.length === 0) return null;
            
            return (
              <div key={categoryIndex} className="border border-gray-200 rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
                
                {/* Kategori Başlığı */}
                <button
                  onClick={() => toggleCategory(categoryIndex)}
                  className={`w-full px-6 py-4 bg-gradient-to-r ${colors.color} text-white flex items-center justify-between hover:opacity-90 transition-opacity`}
                >
                  <div className="flex items-center space-x-3">
                    {icon}
                    <h3 className="text-xl font-bold">{category.category}</h3>
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                      {category.items.length} {t('unit_activities.process')}
                    </span>
                  </div>
                  {expandedCategories[categoryIndex] ? 
                    <ChevronUp className="w-6 h-6" /> : 
                    <ChevronDown className="w-6 h-6" />
                  }
                </button>

                {/* Kategori İçeriği */}
                {expandedCategories[categoryIndex] && (
                  <div className={`${colors.bgColor} p-6`}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {category.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                          
                          {/* Item Başlığı */}
                          <button
                            onClick={() => toggleItem(categoryIndex, itemIndex)}
                            className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-mono">
                                    {item.code}
                                  </span>
                                </div>
                                <h4 className="text-lg font-semibold text-gray-900 leading-tight">
                                  {item.title}
                                </h4>
                              </div>
                              {expandedItems[`${categoryIndex}-${itemIndex}`] ? 
                                <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" /> : 
                                <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" />
                              }
                            </div>
                          </button>

                          {/* Item Detayları */}
                          {expandedItems[`${categoryIndex}-${itemIndex}`] && (
                            <div className="px-6 pb-6">
                              <div className="border-t pt-4">
                                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                                  {item.description}
                                </p>
                                
                                {/* Belge Linkleri */}
                                <div className="flex flex-wrap gap-3">
                                  {item.surecKartiLink && (
                                    <a
                                      href={item.surecKartiLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                                    >
                                      <FileText className="w-4 h-4" />
                                      <span>{t('unit_activities.process_card')}</span>
                                      <ExternalLink className="w-3 h-3" />
                                    </a>
                                  )}
                                  
                                  {item.isAkisSemasiLink && (
                                    <a
                                      href={item.isAkisSemasiLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                                    >
                                      <GitBranch className="w-4 h-4" />
                                      <span>{t('unit_activities.workflow_schema')}</span>
                                      <ExternalLink className="w-3 h-3" />
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Sonuç bulunamadı mesajı */}
        {filteredData.every(category => category.items.length === 0) && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('unit_activities.no_results_title')}</h3>
            <p className="text-gray-500">{t('unit_activities.no_results_description')}</p>
          </div>
        )}

        {/* Alt Bilgi */}
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-8 text-center border border-gray-200">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('unit_activities.iso_section_title')}</h3>
            <p className="text-gray-700 leading-relaxed">
              {t('unit_activities.iso_section_description')}
            </p>
          </div>
        </div>
      </div>
    </PageTemplate>
  );
};

export default BirimFaaliyetleri;